// src/components/ClubRoleBadge.jsx
//
// Pill that labels a user's role on a club. Replaces the two
// near-identical inline RoleBadge components that used to live in
// ClubDashboard.jsx and ClubMembers.jsx.
//
// Accepts either canonical strings (`director`, `club_admin`) or the
// legacy strings that are still in older docs (`owner`, `admin`,
// `member`). Anything unrecognized falls through to the neutral
// "member" style with the literal role as the label, so a new role
// string stays readable instead of silently erroring.
//
// The styling is kept close to what was already in the two pages so
// screenshots / existing designs don't drift.

import React from 'react';
import clsx from 'clsx';
import { CLUB_ROLES, CLUB_ROLE_LABELS, normalizeClubRole } from '@/constants/roles';

const STYLES = {
  // Canonical
  [CLUB_ROLES.DIRECTOR]:   'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  [CLUB_ROLES.CLUB_ADMIN]: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  // Fallback / unrecognized
  member:                  'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300',
};

export default function ClubRoleBadge({ role, className }) {
  const canonical = normalizeClubRole(role);
  const styleKey = canonical || 'member';
  const label = canonical
    ? CLUB_ROLE_LABELS[canonical]
    // No canonical match. Prefer "Member" for the literal 'member' string,
    // and Title-Case anything else so a new role ('coach', etc.) still
    // renders readably.
    : role === 'member' || !role
      ? 'Member'
      : String(role).charAt(0).toUpperCase() + String(role).slice(1);

  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
        STYLES[styleKey] || STYLES.member,
        className
      )}
    >
      {label}
    </span>
  );
}
